import type { TherapyServiceType, TherapyContext } from '../../../lib/therapy';

export type ClientPortalStyle = 'calm' | 'modern';

export interface PortalAppointment {
  id: string;
  start: string; // ISO datetime
  end: string; // ISO datetime
  serviceType: string;
  providerLabel: string;
  /** When known, used to match Episode of Care and messaging threads. */
  providerId?: string;
  episodeOfCareId?: string;
  episodeLabel?: string;
  modality: 'Telehealth' | 'In person';
  status: 'Scheduled' | 'Requested' | 'Completed' | 'Cancelled';
  locationLabel?: string;
  joinUrl?: string;
  /** Therapy service type when available (individual, family, group). */
  therapyServiceType?: TherapyServiceType;
  /** Full normalized therapy context — available when the session has been resolved. */
  therapyContext?: TherapyContext;
  /**
   * Count of Patient + RelatedPerson participants still active on the appointment
   * (not already cancelled / no-show). Used for Couples/Family cancel-self vs everyone.
   */
  activePatientParticipantCount?: number;
}

export interface PortalDocumentItem {
  id: string;
  name: string;
  kind: 'Form' | 'File';
  status: 'Pending' | 'Completed' | 'Shared';
  updated: string; // ISO date
  due?: string; // ISO date
  /** What the client needs to do: fill out the form or sign it. Defaults to 'complete'. */
  action?: 'complete' | 'sign';
  /** Template Library template id (e.g. mock-measure-1) for pending assessments; when set, Start opens assessment taker with this template. */
  templateId?: string;
}

export type PaymentPlanInstallmentStatus = 'paid' | 'upcoming' | 'failed' | 'past_due';

export interface PaymentPlanInstallment {
  installmentNumber: number;
  dueDate: string; // ISO date
  amount: number; // cents
  status: PaymentPlanInstallmentStatus;
}

export interface PaymentPlanPaymentMethod {
  type: 'card' | 'bank';
  brand: string;
  last4: string;
  label: string;
}

export interface PaymentPlan {
  paymentPlanId: string;
  status: 'active' | 'paused' | 'past_due' | 'completed' | 'cancelled';
  totalAmount: number; // cents
  remainingBalance: number; // cents
  installmentAmount: number; // cents
  frequency: 'weekly' | 'biweekly' | 'monthly';
  startDate: string; // ISO date
  nextPaymentDate: string; // ISO date
  lastPaymentDate?: string; // ISO date
  totalInstallments: number;
  completedInstallments: number;
  paymentMethod: PaymentPlanPaymentMethod;
  schedule: PaymentPlanInstallment[];
}

export interface PortalInvoice {
  id: string;
  description: string;
  amountCents: number;
  due: string; // ISO date
  status: 'Due' | 'Paid' | 'Processing';
  paymentPlan?: PaymentPlan;
  /** CPT billing code (e.g. 90847/90846 for family therapy). */
  billingCode?: string;
  /** Therapy service type for this invoice (individual, family, group). */
  therapyServiceType?: TherapyServiceType;
  /** Participant summary label (e.g. "3 participants" for group). */
  participantSummary?: string;
}

export interface PortalStatement {
  id: string;
  label: string;
  period: string;
  availableOn: string; // ISO date
}

// ---------------------------------------------------------------------------
// Mock data (intentionally non-identifying / non-PHI)
// ---------------------------------------------------------------------------


/** Demo rows: include both Telehealth (join + reschedule) and In person (reschedule only) for the portal UI. */
export const mockUpcomingAppointments: PortalAppointment[] = [
  {
    id: 'apt-001',
    start: new Date(Date.now() + 45 * 60 * 1000).toISOString(),
    end: new Date(Date.now() + 105 * 60 * 1000).toISOString(),
    serviceType: 'Individual therapy',
    providerLabel: 'Your therapist',
    providerId: 'ct-therapist',
    episodeOfCareId: 'mock-eoc-ct-therapist',
    episodeLabel: 'Clinician',
    modality: 'Telehealth',
    status: 'Scheduled',
    locationLabel: 'Telehealth',
    joinUrl: '/client-portal/session/apt-001/join',
  },
  {
    id: 'apt-002',
    start: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000 + 50 * 60 * 1000).toISOString(),
    serviceType: 'Medication management',
    providerLabel: 'Practice support',
    providerId: 'ct-support',
    episodeOfCareId: 'mock-eoc-ct-support',
    episodeLabel: 'Scheduling & billing',
    modality: 'In person',
    status: 'Scheduled',
    locationLabel: 'Clinic',
  },
  {
    id: 'apt-003',
    start: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    end: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000 + 50 * 60 * 1000).toISOString(),
    serviceType: 'Telehealth follow-up',
    providerLabel: 'Your therapist',
    providerId: 'ct-therapist',
    episodeOfCareId: 'mock-eoc-ct-therapist',
    episodeLabel: 'Clinician',
    modality: 'Telehealth',
    status: 'Scheduled',
    locationLabel: 'Telehealth',
    joinUrl: '/client-portal/session/apt-003/join',
  },
];

export const mockPastAppointments: PortalAppointment[] = [
  {
    id: 'apt-101',
    start: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    end: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000 + 50 * 60 * 1000).toISOString(),
    serviceType: 'Individual therapy',
    providerLabel: 'Your therapist',
    providerId: 'ct-therapist',
    episodeOfCareId: 'mock-eoc-ct-therapist',
    episodeLabel: 'Clinician',
    modality: 'Telehealth',
    status: 'Completed',
    locationLabel: 'Telehealth',
  },
  {
    id: 'apt-102',
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    end: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000 + 45 * 60 * 1000).toISOString(),
    serviceType: 'Intake visit',
    providerLabel: 'Practice support',
    providerId: 'ct-support',
    episodeOfCareId: 'mock-eoc-ct-support',
    episodeLabel: 'Scheduling & billing',
    modality: 'In person',
    status: 'Completed',
    locationLabel: 'Main clinic',
  },
];

export const mockDocuments: PortalDocumentItem[] = [
  {
    id: 'doc-form-phq9',
    name: 'PHQ-9 (Depression Screening)',
    kind: 'Form',
    status: 'Pending',
    action: 'complete',
    templateId: 'mock-measure-1',
    updated: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    due: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'doc-form-003',
    name: 'Informed Consent for Treatment',
    kind: 'Form',
    status: 'Pending',
    action: 'sign',
    updated: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    due: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'doc-form-004',
    name: 'Telehealth Consent',
    kind: 'Form',
    status: 'Pending',
    action: 'sign',
    updated: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    due: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'doc-form-001',
    name: 'HIPAA Acknowledgment',
    kind: 'Form',
    status: 'Completed',
    action: 'sign',
    updated: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'doc-form-002',
    name: 'GAD-7 (Anxiety Screening)',
    kind: 'Form',
    status: 'Completed',
    updated: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'doc-file-001',
    name: 'Notice of Privacy Practices',
    kind: 'File',
    status: 'Shared',
    updated: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

export const mockInvoices: PortalInvoice[] = [
  {
    id: 'inv-001',
    description: 'Session copay',
    amountCents: 4500,
    due: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    status: 'Due',
  },
  {
    id: 'inv-002',
    description: 'Late cancellation fee',
    amountCents: 2500,
    due: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    status: 'Paid',
  },
  {
    id: 'inv-003',
    description: 'Therapy sessions — payment plan',
    amountCents: 45000,
    due: '2026-04-15T00:00:00.000Z',
    status: 'Due',
    paymentPlan: {
      paymentPlanId: 'pp-001',
      status: 'active',
      totalAmount: 45000,
      remainingBalance: 30000,
      installmentAmount: 7500,
      frequency: 'monthly',
      startDate: '2026-01-15',
      nextPaymentDate: '2026-04-15',
      lastPaymentDate: '2026-02-15',
      totalInstallments: 6,
      completedInstallments: 2,
      paymentMethod: {
        type: 'card',
        brand: 'Visa',
        last4: '4242',
        label: 'Visa ending in 4242',
      },
      schedule: [
        { installmentNumber: 1, dueDate: '2026-01-15', amount: 7500, status: 'paid' },
        { installmentNumber: 2, dueDate: '2026-02-15', amount: 7500, status: 'paid' },
        { installmentNumber: 3, dueDate: '2026-03-15', amount: 7500, status: 'failed' },
        { installmentNumber: 4, dueDate: '2026-04-15', amount: 7500, status: 'upcoming' },
        { installmentNumber: 5, dueDate: '2026-05-15', amount: 7500, status: 'upcoming' },
        { installmentNumber: 6, dueDate: '2026-06-15', amount: 7500, status: 'upcoming' },
      ],
    },
  },
];

/** Invoices demonstrating therapy-type-specific billing fields. */
export const mockTherapyInvoices: PortalInvoice[] = [
  {
    id: 'inv-ind-001',
    description: 'Individual therapy session',
    amountCents: 18000,
    due: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
    status: 'Due',
    billingCode: '90837',
    therapyServiceType: 'individual',
  },
  {
    id: 'inv-fam-present-001',
    description: 'Family therapy — IP present',
    amountCents: 22000,
    due: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000).toISOString(),
    status: 'Due',
    billingCode: '90847',
    therapyServiceType: 'family',
    participantSummary: '3 family members + 1 practitioner',
  },
  {
    id: 'inv-fam-absent-001',
    description: 'Family therapy — IP absent',
    amountCents: 22000,
    due: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
    status: 'Paid',
    billingCode: '90846',
    therapyServiceType: 'family',
    participantSummary: '2 family members + 1 practitioner',
  },
  {
    id: 'inv-grp-001',
    description: 'Group therapy session',
    amountCents: 8000,
    due: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    status: 'Paid',
    billingCode: '90853',
    therapyServiceType: 'group',
    participantSummary: '3 patients, 2 practitioners',
  },
];

export const mockStatements: PortalStatement[] = [
  {
    id: 'stmt-2026-01',
    label: 'Statement',
    period: 'Jan 2026',
    availableOn: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'stmt-2025-12',
    label: 'Statement',
    period: 'Dec 2025',
    availableOn: new Date(Date.now() - 50 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

export interface PortalSuperbill {
  id: string;
  /** Display label, e.g. "February 2026" */
  month: string;
  /** Year-month key for sorting, e.g. "2026-02" */
  yearMonth: string;
}

export const mockSuperbills: PortalSuperbill[] = [
  { id: 'sb-2026-02', month: 'February 2026', yearMonth: '2026-02' },
  { id: 'sb-2026-01', month: 'January 2026', yearMonth: '2026-01' },
  { id: 'sb-2025-12', month: 'December 2025', yearMonth: '2025-12' },
];

export interface PortalAssessment {
  id: string;
  name: string;
  status: 'Pending' | 'Completed';
  assignedDate: string; // ISO date
  dueDate?: string; // ISO date
  completedDate?: string; // ISO date
  /** Mock template id to launch AssessmentFillPage. */
  templateId?: string;
}

export const mockAssessments: PortalAssessment[] = [
  {
    id: 'assess-001',
    name: 'PHQ-9 (Depression Screening)',
    status: 'Pending',
    assignedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    templateId: 'mock-measure-1',
  },
  {
    id: 'assess-002',
    name: 'GAD-7 (Anxiety Screening)',
    status: 'Pending',
    assignedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    templateId: 'mock-measure-2',
  },
];
